import {observer} from 'mobx-react-lite'
import React, {useEffect, useMemo} from 'react'
import {Lang} from 'yougile-lang'
import {Accordion, AccordionItemData} from '@components/common/base/accordion'
import {Help} from '@components/common/base/help'
import {NewProjectPopup} from '@components/project-participants/new-project-popup'
import {useSyncEvent} from '@containers/hooks'
import {Panel} from '../common/panel'
import {
  CompanyProjectsStore,
  useCompanyProjectsStore
} from './company-projects-store'
import {PanelMenu} from './panel-menu'
import {ProjectGroupTitle} from './project-group-title'
import {ProjectImport} from './project-import'
import {
  AdminProjectList,
  ArchivedProjectList,
  CurrentProjectList
} from './project-list'

const useAccordionItems = (store: CompanyProjectsStore): AccordionItemData[] =>
  useMemo(
    () => [
      {
        id: 'admin-projects',
        title: (
          <ProjectGroupTitle
            title={Lang.msg.proj.adminProjects}
            count={store.state.listForAdmin.length}
            help="adminProjects"
          />
        ),
        content: <AdminProjectList />,
        visible: !!store.state.listForAdmin.length
      },
      {
        id: 'archived-projects',
        title: (
          <ProjectGroupTitle
            title={Lang.msg.proj.archivedProjects}
            count={store.state.listArchived.length}
          />
        ),
        content: <ArchivedProjectList />,
        visible: !!store.state.listArchived.length
      }
    ],
    [store.state.listForAdmin, store.state.listArchived, Lang.msg]
  )

/**
 * Preventing `dragover` removes default animation in Chrome on drag end which
 * behaves not as desired
 */
const useFixDnD = (isDragging: boolean): void =>
  useEffect(() => {
    if (isDragging) {
      const fn = (e: DragEvent) => e.preventDefault()

      document.addEventListener('dragover', fn)
      return () => document.removeEventListener('dragover', fn)
    }

    return () => void 0
  }, [isDragging])

export const CompanyProjects = observer(({className}: {className?: string}) => {
  const [state, store] = useCompanyProjectsStore()
  const accordionItems = useAccordionItems(store)

  useEffect(() => {
    store.calculateLists()
  }, [])

  useSyncEvent(
    'current',
    {
      debounceTime: 50,
      filter: '*'
    },
    () => store.calculateLists()
  )

  useFixDnD(!!state.projectsDraggedProjectId)

  return (
    <Panel
      title={Lang.msg.companyProjects}
      header={
        <div className="flex items-center justify-end w-full">
          <Help name="projects" />
        </div>
      }
      className={className}
    >
      {state.newProjIsOpened && (
        <NewProjectPopup close={store.newProjClose} type={state.newProjType} />
      )}
      {state.projectImportIsOpen && <ProjectImport />}

      <PanelMenu />
      <CurrentProjectList />
      <Accordion multi items={accordionItems} className="mt-16" />
    </Panel>
  )
})
